// Packages
import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import { connect } from "react-redux";
import { useJsApiLoader } from "@react-google-maps/api";
import { debounce } from "throttle-debounce";
import { FormattedMessage } from "react-intl";
// Helpers, Utils etc.
import { deepCopyFunction } from "../../helpers/utils";
// Contexts
import { LocaleContext } from "../../wrappers/localeWrapper";
import { EnvContext } from "../../components/envContext";

declare global {
  interface Window {
    google: any;
  }
}

interface IGoogleMapAreaSearchProps {
  options?: any;
  onChange?: any;
  updateAreaError: any;
  resetDeliveryAreaSelection: any;
}

const LIBRARIES: ("places" | "geometry")[] = ["places", "geometry"];
const DEBOUNCE_TIME = 400;
const MINIMUM_CHARACTERS = 3;

// Inner component that uses the Google Maps API after it's loaded
function GoogleMapAreaSearchContent(props: IGoogleMapAreaSearchProps) {
  const [inputValue, setInputValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | null>(null);
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedSetSearchQuery = useCallback(
    debounce(DEBOUNCE_TIME, (value: string) => {
      setSearchQuery(value);
    }),
    []
  );

  useEffect(() => {
    props.resetDeliveryAreaSelection();
  }, []);

  // Initialize services when component mounts (API is already loaded)
  useEffect(() => {
    if (window.google?.maps?.places) {
      const autocomplete = new window.google.maps.places.AutocompleteService();
      setAutocompleteService(autocomplete);

      // PlacesService needs a DOM element
      const dummyDiv = document.createElement("div");
      const places = new window.google.maps.places.PlacesService(dummyDiv);
      setPlacesService(places);

      // Focus the input after services are ready to prompt user to start typing
      document.getElementById("address-search-input")?.focus();
    }
  }, []);

  // Handle clicks/touches outside to close dropdown (supports both mouse and touch)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    // Add both mouse and touch event listeners for cross-platform support
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, { passive: true });

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // Fetch predictions when searchQuery changes (after debounce)
  useEffect(() => {
    if (searchQuery.length >= MINIMUM_CHARACTERS && autocompleteService) {
      setIsLoading(true);
      autocompleteService.getPlacePredictions(
        { input: searchQuery },
        (predictions, status) => {
          setIsLoading(false);
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions);
            setShowDropdown(true);
          } else {
            setSuggestions([]);
            setShowDropdown(false);
          }
        }
      );
    } else {
      setSuggestions([]);
      setShowDropdown(false);
      setIsLoading(false);
    }
  }, [searchQuery, autocompleteService]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    props.updateAreaError("");

    if (value.length >= MINIMUM_CHARACTERS) {
      setIsLoading(true);
    }

    debouncedSetSearchQuery(value);
  };

  const handleSuggestionClick = (suggestion: google.maps.places.AutocompletePrediction) => {
    if (!placesService) return;

    setInputValue(suggestion.description);
    setShowDropdown(false);
    setSuggestions([]);

    placesService.getDetails(
      { placeId: suggestion.place_id, fields: ["geometry"] },
      (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          findServiceableArea(lat, lng, suggestion.description);
        }
      }
    );
  };

  const findServiceableArea = (lat: any, lng: any, address: any) => {
    let parsedPosition = new window.google.maps.LatLng(lat, lng);
    let areaOptions = deepCopyFunction(props.options);
    let serviceableArea = areaOptions.find((option: any) => {
      if (option.mapped_zones && option.mapped_zones.length) {
        let zonePolygon = new google.maps.Polygon({
          paths: option.mapped_zones,
        });
        return window.google.maps.geometry.poly.containsLocation(
          parsedPosition,
          zonePolygon
        );
      }
      return false;
    });
    if (serviceableArea) {
      props.onChange(serviceableArea.value, address);
      let selectedAddress = JSON.stringify({
        lat: lat,
        lng: lng,
        address: address,
      });
      localStorage.setItem("selectedAddress", selectedAddress);
    } else {
      props.resetDeliveryAreaSelection();
      props.updateAreaError("address");
    }
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowDropdown(true);
    }
  };

  return (
    <div className="google-map-autocomplete-container-mobile" ref={containerRef}>
      <input
        className="form-control custom-input"
        type="text"
        id="address-search-input"
        placeholder="eg: Chesterfield"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        autoComplete="off"
      />
      {isLoading && (
        <span className="autocomplete-loading-indicator">
          <span className="spinner"></span>
        </span>
      )}
      {showDropdown && suggestions.length > 0 && (
        <ul className="autocomplete-suggestions-dropdown">
          {suggestions.map((suggestion) => (
            <li
              key={suggestion.place_id}
              onClick={() => handleSuggestionClick(suggestion)}
              className="autocomplete-suggestion-item"
            >
              {suggestion.description}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Wrapper component that handles loading the Google Maps API
function GoogleMapAreaSearch(props: IGoogleMapAreaSearchProps) {
  const { googleMapKey } = useContext(EnvContext);
  const localeContext = useContext(LocaleContext) as { locale: string; updateLocale: (locale: string) => void };

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: googleMapKey || "",
    libraries: LIBRARIES,
    language: localeContext.locale,
  });

  if (!googleMapKey) {
    return null;
  }

  if (loadError) {
    return <div><FormattedMessage id="maps.error_loading" /></div>;
  }

  if (!isLoaded) {
    return <div className="google-map-autocomplete-container-mobile">
      <input
        className="form-control custom-input"
        type="text"
        placeholder="..."
        disabled
      />
    </div>;
  }

  return <GoogleMapAreaSearchContent {...props} />;
}

export default connect(null)(GoogleMapAreaSearch);
